import React, { useRef, useState, useCallback, useEffect } from 'react';
import { UploadOutlined } from '@ant-design/icons';
import { Handle, Position } from '@xyflow/react';
import { message } from 'antd';
import AddNodePopover, { FUNCTION_NODE_GROUPS } from '../../components/AddNodePopover';
import { uploadFlowImage } from '../../execution';
import Utils from '@utils/utils';
import styles from './index.module.scss';

const HANDLE_ICON = 'https://photostudio-ai-oss.arcsoft.com.cn//photostudio-opshub/FreeCanvas/menu.svg';
const UPLOAD_TEXT = '上传图片';
const REUPLOAD_TEXT = '重新上传';

const RIGHT_HANDLE_CX = 262;
const HANDLE_CY = 160;

const MAGNET_RADIUS = 80;
const MAX_PULL = 18;
const TITLE_MAX_LENGTH = 10;
const TITLE_PATTERN = /^[A-Za-z0-9\u4e00-\u9fa5]+$/;

const computeHandleOffset = (mx, my, cx, cy, side) => {
    const dx = mx - cx;
    const dy = my - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist >= MAGNET_RADIUS || dist < 0.5) return { x: 0, y: 0 };

    const pull = MAX_PULL * (1 - dist / MAGNET_RADIUS);
    let offsetX = (dx / dist) * pull;
    const offsetY = (dy / dist) * pull;

    if (side === 'left' && offsetX > 0) offsetX = 0;
    if (side === 'right' && offsetX < 0) offsetX = 0;

    return { x: offsetX, y: offsetY };
};

const HANDLE_BASE_STYLE = {
    position: 'absolute',
    width: 36,
    height: 36,
    background: 'transparent',
    border: 'none',
    borderRadius: 0,
    top: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 36,
    minHeight: 36,
    transition: 'opacity 0.16s ease, transform 0.16s ease',
};

const ImageNode = ({ id, data, selected }) => {
    const thumbnailSrc = data.src ? Utils.getImgThumbnail(data.src) : '';
    const inputRef = useRef(null);
    const wrapperRef = useRef(null);
    const hideTimerRef = useRef(null);
    const [rightOffset, setRightOffset] = useState({ x: 0, y: 0 });
    const [hovered, setHovered] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [editingTitle, setEditingTitle] = useState(false);
    const [draftTitle, setDraftTitle] = useState(data.imageSequenceLabel || data.label || '');
    const titleInputRef = useRef(null);

    const showHandles = hovered || !!selected;
    const interactionLocked = !!data.interactionLocked;
    const groupExecutionLocked = !!data.groupExecutionLocked;
    const readOnlyView = !!data.readOnlyView;
    const displayTitle = data.imageSequenceLabel || data.label || '';

    useEffect(() => {
        setDraftTitle(displayTitle);
    }, [displayTitle]);

    useEffect(() => {
        if (!editingTitle) return;
        titleInputRef.current?.focus();
        titleInputRef.current?.select();
    }, [editingTitle]);

    const handleEnter = useCallback(() => {
        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
        setHovered(true);
    }, []);

    const handleLeave = useCallback(() => {
        hideTimerRef.current = setTimeout(() => {
            setHovered(false);
            setRightOffset({ x: 0, y: 0 });
            hideTimerRef.current = null;
        }, 120);
    }, []);

    const handleMouseMove = useCallback((event) => {
        const rect = wrapperRef.current?.getBoundingClientRect();
        if (!rect || rect.width === 0) return;

        const scale = rect.width / 240;
        const mx = (event.clientX - rect.left) / scale;
        const my = (event.clientY - rect.top) / scale;
        setRightOffset(computeHandleOffset(mx, my, RIGHT_HANDLE_CX, HANDLE_CY, 'right'));

        const dxR = mx - RIGHT_HANDLE_CX;
        const dyR = my - HANDLE_CY;
        const inMagnet = Math.sqrt(dxR * dxR + dyR * dyR) < MAGNET_RADIUS;
        if (inMagnet) {
            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
            }
            setHovered(true);
        }
    }, []);

    const handleOpenPicker = useCallback((event) => {
        if (groupExecutionLocked || interactionLocked || readOnlyView) return;
        event?.preventDefault?.();
        event?.stopPropagation?.();

        const inputElement = inputRef.current;
        if (!inputElement) return;

        if (typeof inputElement.showPicker === 'function') {
            try {
                inputElement.showPicker();
                return;
            } catch (error) {
                // Fall back to click() when showPicker is unavailable or blocked.
            }
        }

        inputElement.click();
    }, [groupExecutionLocked, interactionLocked, readOnlyView]);

    const handleUploadFile = useCallback(async (file) => {
        if (!file) return;

        const src = await uploadFlowImage(file);
        if (!src) return;

        data.onChange?.(id, {
            src,
            inputParams: {
                ...(data.inputParams || {}),
                output: src,
            },
        });
    }, [data, id]);

    const handleFileChange = useCallback(async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        await handleUploadFile(file);
    }, [handleUploadFile]);

    const handleDragOver = useCallback((event) => {
        if (groupExecutionLocked || interactionLocked) return;
        const hasFiles = Array.from(event.dataTransfer?.items || []).some((item) => item.kind === 'file');
        if (!hasFiles) return;

        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = 'copy';
        setDragOver(true);
    }, [groupExecutionLocked, interactionLocked]);

    const handleDragLeave = useCallback((event) => {
        if (!wrapperRef.current?.contains(event.relatedTarget)) {
            setDragOver(false);
        }
    }, []);

    const handleDrop = useCallback(async (event) => {
        if (groupExecutionLocked || interactionLocked) return;

        const file = Array.from(event.dataTransfer?.files || [])[0];
        setDragOver(false);
        if (!file) return;

        event.preventDefault();
        event.stopPropagation();
        await handleUploadFile(file);
    }, [groupExecutionLocked, handleUploadFile, interactionLocked]);

    const commitTitleChange = useCallback(() => {
        const nextTitle = String(draftTitle || '').trim();
        setEditingTitle(false);

        if (!nextTitle || nextTitle === displayTitle) {
            setDraftTitle(displayTitle);
            return;
        }

        if (nextTitle.length > TITLE_MAX_LENGTH || !TITLE_PATTERN.test(nextTitle)) {
            message.warning('仅支持中文、英文和数字，最长10个字符');
            setDraftTitle(displayTitle);
            return;
        }

        data.onChange?.(id, {
            label: nextTitle,
            imageSequenceLabel: nextTitle,
        });
    }, [data, displayTitle, draftTitle, id]);

    const handleTitleKeyDown = useCallback((event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            commitTitleChange();
            return;
        }
        if (event.key === 'Escape') {
            setEditingTitle(false);
            setDraftTitle(displayTitle);
        }
    }, [commitTitleChange, displayTitle]);

    const handleClassName = `${styles.handle} ${!showHandles ? styles.handleHidden : ''}`;

    return (
        <div
            ref={wrapperRef}
            className={styles.wrapper}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
            onMouseMove={handleMouseMove}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <div
                className={`${styles.sideCapture} ${styles.sideCaptureRight}`}
                aria-hidden="true"
            />

            <div className={styles.label}>
                {editingTitle ? (
                    <input
                        ref={titleInputRef}
                        value={draftTitle}
                        maxLength={TITLE_MAX_LENGTH}
                        className={styles.labelInput}
                        onChange={(event) => setDraftTitle(event.target.value.replace(/\s+/g, ''))}
                        onBlur={commitTitleChange}
                        onKeyDown={handleTitleKeyDown}
                        onClick={(event) => event.stopPropagation()}
                    />
                ) : (
                    <button
                        type="button"
                        className={styles.labelButton}
                        onClick={(event) => {
                            event.stopPropagation();
                            if (readOnlyView || interactionLocked) return;
                            setEditingTitle(true);
                        }}
                    >
                        {displayTitle}
                    </button>
                )}
            </div>

            <AddNodePopover
                placement="rightTop"
                nodeGroups={FUNCTION_NODE_GROUPS}
                showImageItem={false}
                title="引用该节点生成"
                onSelect={(nodeType) => {
                    if (interactionLocked || readOnlyView) return;
                    data.onQuickAddNode?.(id, nodeType);
                }}
            >
                <Handle
                    type="source"
                    position={Position.Right}
                    isConnectableStart={!interactionLocked}
                    className={handleClassName}
                    style={{
                        ...HANDLE_BASE_STYLE,
                        right: -40,
                        left: 'auto',
                        transform: `translateY(-50%) translate(${rightOffset.x}px, ${rightOffset.y}px)`,
                    }}
                >
                    <span
                        className={styles.handleIcon}
                        aria-hidden="true"
                        style={{ '--handle-icon-url': `url(${HANDLE_ICON})` }}
                    />
                </Handle>
            </AddNodePopover>

            <div className={`${styles.card} ${selected ? styles.cardSelected : ''} ${dragOver ? styles.cardDragOver : ''}`}>
                {data.src ? (
                    <>
                        <img src={thumbnailSrc} alt={data.label} draggable={false} className={styles.image} />
                        <button
                            type="button"
                            className={`${styles.reuploadButton} nodrag nopan free-canvas-clickable`}
                            onMouseDown={handleOpenPicker}
                            onClick={handleOpenPicker}
                        >
                            <UploadOutlined className={styles.reuploadIcon} />
                            {REUPLOAD_TEXT}
                        </button>
                    </>
                ) : (
                    <button
                        type="button"
                        className={`${styles.uploadButton} nodrag nopan free-canvas-clickable`}
                        onMouseDown={handleOpenPicker}
                        onClick={handleOpenPicker}
                    >
                        <UploadOutlined className={styles.uploadIcon} />
                        <span>{UPLOAD_TEXT}</span>
                    </button>
                )}

                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className={styles.fileInput}
                />
            </div>
        </div>
    );
};

export default ImageNode;
